/**
 * SmartSearchResults — the rendering half of the smart search.
 *
 * One query can legitimately answer with three different kinds of thing at
 * once: "mega" is an era, a set, and about forty cards. So they are grouped
 * under their own headings and every row carries a kind badge, rather than
 * being poured into one list where a set named like a card is a trap. Nobody
 * has to pick a category before they see anything.
 *
 * Presentational only. It owns no query, no debounce and no input — pass it
 * the result of useSmartSearch and three handlers. That is what lets the same
 * results appear on the Explore-tab menu, in the scanner, and later in the All
 * tab and Trade Now.
 *
 * The CARD half is not drawn here. It is CatalogCardGrid, the same component
 * the flat "All Cards" list uses, which is what makes the zoom preference, the
 * separator headings and the price chip identical on both screens instead of
 * being two implementations that drift. This file had its own three-column grid
 * with no zoom in it at all, and that was the bug: "if i search i notice the
 * cards break our zoom preference and theres no way to change it".
 */
import { Layers, Library, Search } from 'lucide-react';
import { TcgPocketLine } from '../TcgPocketNotice';
import { isTcgPocketSet, TCG_POCKET_ERA_ID } from '../../utils/tcgPocket';
import { formatMoney } from '../../utils/catalogPrices';
import { catalogGrouping } from '../../services/cardData/catalogFilters';
import CatalogCardGrid from './CatalogCardGrid';

const KIND_LABEL = { era: 'Era', set: 'Set', card: 'Card' };

const styles = {
  wrap: { display: 'flex', flexDirection: 'column', paddingBottom: '8px' },
  sectionHead: {
    display: 'flex', alignItems: 'center', gap: '7px',
    padding: '14px 2px 8px',
    fontSize: '11px', fontWeight: '700', letterSpacing: '0.06em',
    textTransform: 'uppercase', color: 'var(--text-secondary)',
  },
  rowList: { display: 'flex', flexDirection: 'column', gap: '6px' },
  row: {
    display: 'flex', alignItems: 'center', gap: '11px',
    padding: '9px 11px', width: '100%', minWidth: 0,
    background: 'var(--bg-card)', border: '1px solid var(--border)',
    borderRadius: '11px', color: 'var(--text)',
    textAlign: 'left', cursor: 'pointer',
    WebkitTapHighlightColor: 'transparent',
  },
  rowArt: {
    width: '54px', height: '36px', flexShrink: 0,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: '3px', boxSizing: 'border-box',
    borderRadius: '7px', background: 'var(--bg-sheet)', overflow: 'hidden',
  },
  rowArtImg: { maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' },
  rowBody: { flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '2px' },
  rowName: {
    fontSize: '13.5px', fontWeight: '650', color: 'var(--text)',
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0,
  },
  rowMeta: {
    fontSize: '11px', color: 'var(--text-secondary)',
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0,
  },
  rowValue: {
    fontSize: '11px', fontWeight: '600', color: '#4ade80',
    fontVariantNumeric: 'tabular-nums',
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0,
  },
  kindBadge: {
    flexShrink: 0, alignSelf: 'center',
    padding: '3px 7px', borderRadius: '999px',
    border: '1px solid var(--border)', background: 'var(--bg-sheet)',
    fontSize: '9.5px', fontWeight: '700', letterSpacing: '0.05em',
    textTransform: 'uppercase', color: 'var(--text-secondary)',
  },
  sectionCount: {
    marginLeft: 'auto',
    fontSize: '11px', fontWeight: '700', letterSpacing: '0.04em',
    color: 'var(--text-muted, var(--text-secondary))',
    fontVariantNumeric: 'tabular-nums',
  },
  // What is narrowing the results, in one line, with the way out on it. Without
  // it the only clue that a filter has cut 305 Magikarps down to four is the
  // count beside the Cards heading.
  filterNote: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px',
    width: '100%', marginBottom: '8px', padding: '7px 10px',
    borderWidth: '1px', borderStyle: 'solid', borderColor: 'var(--border)',
    borderRadius: '9px', background: 'var(--bg-card)',
    cursor: 'pointer', textAlign: 'left', WebkitTapHighlightColor: 'transparent',
  },
  filterNoteText: {
    fontSize: '12px', fontWeight: '600', color: 'var(--text)',
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0,
  },
  filterNoteClear: { fontSize: '11px', fontWeight: '600', color: 'var(--purple)', flexShrink: 0 },
  // The dynamic loading copy, not a spinner: the owner's "Sorting 22K a-z".
  // Sits UNDER the cards it is re-ordering, so a sort never blanks the screen.
  status: { display: 'flex', alignItems: 'center', gap: '8px', padding: '14px 2px' },
  statusDot: {
    width: '8px', height: '8px', borderRadius: '50%',
    backgroundColor: 'var(--purple)', animation: 'pulse 1.1s ease-in-out infinite',
    flexShrink: 0,
  },
  statusText: {
    fontSize: '13px', fontWeight: '500', color: 'var(--text-secondary)',
    fontVariantNumeric: 'tabular-nums',
  },
  state: {
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px',
    padding: '40px 20px', textAlign: 'center',
    fontSize: '13px', color: 'var(--text-secondary)',
  },
  stateHint: { fontSize: '11.5px', color: 'var(--text-muted, var(--text-secondary))', maxWidth: '260px', lineHeight: 1.45 },
};

const Section = ({ icon, label, count = null, children }) => (
  <>
    <div style={styles.sectionHead}>
      {icon}{label}
      {count != null && <span style={styles.sectionCount}>{count.toLocaleString()}</span>}
    </div>
    {children}
  </>
);

/**
 * @param {{
 *   result: object,                 the return value of useSmartSearch
 *   theme: object,
 *   isDark?: boolean,
 *   onPickEra?: (era) => void,
 *   onPickSet?: (set) => void,
 *   onPickCard?: (card) => void,
 *   onHoldCard?: (card) => void,   hold to wish for it, same seam as the tap
 *   ownedCardIds?: Set<string>,
 *   idleHint?: string,
 *   pricingEnabled?: boolean,
 *   setTotals?: Record<string, {total_near_mint: number}>,
 *   eraValues?: Record<string, number>,
 *   zoom?: 0|1|2,                   the shared 3-step card size
 *   sort?: string|null,             which order the cards came back in
 *   eras?: object[]|null,           for the dual era/set separator
 *   status?: string|null,           the dynamic loading copy, e.g. "Sorting 305 cards by value"
 *   filterNote?: {text: string, onClear: () => void}|null,
 *   selectedCardIds?: Set<string>|null,
 * }} props
 */
export default function SmartSearchResults({
  result,
  theme,
  isDark = true,
  onPickEra,
  onPickSet,
  onPickCard,
  onHoldCard = null,
  ownedCardIds = null,
  idleHint = 'Try a name, a set, a card number, or an era — "char 004", "evolutions", "wizards of the coast".',
  // Money, on every kind of hit. The three quantities are different sizes of
  // the same thing - one card, one set, one era - so each one names its own
  // scope rather than leaving three bare green numbers to be told apart by
  // where they happen to sit.
  pricingEnabled = false,
  setTotals = null,
  eraValues = null,
  zoom = 0,
  sort = null,
  eras = null,
  status = null,
  filterNote = null,
  // Lifted, not owned. The picker that will reuse this surface — "Add N cards
  // to <binder>" — passes the selection down and takes the taps through
  // onPickCard, so neither has to be retrofitted into the grid later.
  selectedCardIds = null,
}) {
  const { active, loading, reordering, eraHits, setHits, cardHits, empty, parsed, matchTotal } = result;

  if (!active) {
    return (
      <div style={styles.state}>
        <Search size={20} strokeWidth={2} />
        <span style={styles.stateHint}>{idleHint}</span>
      </div>
    );
  }

  // Eras and sets are matched in memory, so they are on screen before the card
  // query has even been sent. Only say "searching" when there is nothing yet.
  //
  // A filter on makes "nothing matches" the wrong sentence: the query matched,
  // and the ticks are what emptied it. That case falls through to the Cards
  // section, which says so and offers the way out.
  if (empty && !filterNote) {
    return (
      <div style={styles.state}>
        <Search size={20} strokeWidth={2} />
        <span>Nothing matches “{parsed.raw}”.</span>
        <span style={styles.stateHint}>
          Check the spelling, or try just the card number.
        </span>
      </div>
    );
  }

  return (
    <div style={styles.wrap}>
      {eraHits.length > 0 && (
        <Section icon={<Layers size={13} strokeWidth={2.4} />} label="Eras">
          <div style={styles.rowList}>
            {eraHits.map((era) => (
              <button key={era.id} style={styles.row} onClick={() => onPickEra && onPickEra(era)}>
                <div style={styles.rowArt}>
                  {era.logo_url
                    ? <img src={`${era.logo_url}.png`} alt="" style={styles.rowArtImg} loading="lazy"
                           onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                    : <Layers size={16} strokeWidth={2} color="var(--text-secondary)" />}
                </div>
                <div style={styles.rowBody}>
                  <span style={styles.rowName}>{era.display_name || era.id}</span>
                  <span style={styles.rowMeta}>
                    {era.start_year ? `${era.start_year}${era.end_year ? `–${era.end_year}` : '–now'}` : 'Era'}
                  </span>
                  {pricingEnabled && era.id === TCG_POCKET_ERA_ID ? (
                    <TcgPocketLine theme={theme} isDark={isDark} compact />
                  ) : (
                    pricingEnabled && formatMoney(eraValues?.[era.id]) && (
                      <span style={styles.rowValue}>Era value {formatMoney(eraValues[era.id])}</span>
                    )
                  )}
                </div>
                <span style={styles.kindBadge}>{KIND_LABEL.era}</span>
              </button>
            ))}
          </div>
        </Section>
      )}

      {setHits.length > 0 && (
        <Section icon={<Library size={13} strokeWidth={2.4} />} label="Sets">
          <div style={styles.rowList}>
            {setHits.map((set) => {
              const logo = set.logo_url || set.logo;
              const year = String(set.release_date || set.releaseDate || '').slice(0, 4);
              const total = set.card_count_official || set.card_count_total
                || set.cardCount?.official || set.cardCount?.total || null;
              return (
                <button key={set.id} style={styles.row} onClick={() => onPickSet && onPickSet(set)}>
                  <div style={styles.rowArt}>
                    {logo
                      ? <img src={`${logo}.png`} alt="" style={styles.rowArtImg} loading="lazy"
                             onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                      : <Library size={16} strokeWidth={2} color="var(--text-secondary)" />}
                  </div>
                  <div style={styles.rowBody}>
                    <span style={styles.rowName}>{set.name}</span>
                    <span style={styles.rowMeta}>
                      {[total ? `${total} cards` : null, year || null].filter(Boolean).join(' · ') || 'Set'}
                    </span>
                    {pricingEnabled && isTcgPocketSet(set.id) ? (
                      <TcgPocketLine theme={theme} isDark={isDark} compact />
                    ) : (
                      pricingEnabled && formatMoney(setTotals?.[set.id]?.total_near_mint) && (
                        <span style={styles.rowValue}>
                          Set value {formatMoney(setTotals[set.id].total_near_mint)}
                        </span>
                      )
                    )}
                  </div>
                  <span style={styles.kindBadge}>{KIND_LABEL.set}</span>
                </button>
              );
            })}
          </div>
        </Section>
      )}

      {(cardHits.length > 0 || loading || Boolean(filterNote)) && (
        <Section
          icon={<Search size={13} strokeWidth={2.4} />}
          label="Cards"
          count={matchTotal}
        >
          {filterNote && (
            <button style={styles.filterNote} onClick={filterNote.onClear}>
              <span style={styles.filterNoteText}>{filterNote.text}</span>
              <span style={styles.filterNoteClear}>Clear</span>
            </button>
          )}
          {/* The only moment this screen has nothing to show. A re-sort or a
              filter change keeps its cards and puts the status line underneath
              them instead, which is the difference between a screen that is
              working and a screen that has blanked. */}
          {loading && !reordering && !cardHits.length && (
            <div style={{ ...styles.state, padding: '20px' }}>{status || 'Searching cards…'}</div>
          )}
          <CatalogCardGrid
            cards={cardHits}
            zoom={zoom}
            wantPrintings={parsed?.printings || null}
            groupMode={catalogGrouping(sort)}
            eras={eras}
            theme={theme}
            isDark={isDark}
            pricingEnabled={pricingEnabled}
            ownedCardIds={ownedCardIds}
            onCardTap={onPickCard}
            onCardHold={onHoldCard}
            selectedIds={selectedCardIds}
            /* The same clearance the catalogue grid carries, so the last row is
               not sitting under the bottom nav on a phone. */
            style={{ paddingBottom: 'var(--nav-clearance)' }}
          />
          {!cardHits.length && !loading && (
            <div style={{ ...styles.state, padding: '20px' }}>
              No cards match all of that. Try unticking one.
            </div>
          )}
          {status && cardHits.length > 0 && (
            <div style={styles.status}>
              <div style={styles.statusDot} />
              <span style={styles.statusText}>{status}</span>
            </div>
          )}
        </Section>
      )}
    </div>
  );
}
